// قسم حسابات موحّد لقائمة الدخل والميزانية العمومية (شاشة + طباعة)
import React from 'react';
import { Paper, Box, Typography } from '@mui/material';
import { fmtMoney } from '../../components/Reports/ReportKit';
import { P } from '../../components/Reports/PrintKit';

// مسار صفحة كشف الحساب في App.jsx — عدّله إن كان مختلفًا عندك
export const STATEMENT_ROUTE = '/account-statement';

const BORDER = '#e5e7eb';

/**
 * @param rows   [{ accountId, accountCode, accountName, amount }]
 * @param onRowClick  (row) => void  — لفتح كشف الحساب
 */
export function FinancialSection({ title, rows = [], total, color = '#4f46e5', icon, onRowClick, emptyText = 'لا توجد بيانات' }) {
  const base = Math.abs(Number(total)) || 1;
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fafbfc' }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '9px', display: 'grid', placeItems: 'center',
          bgcolor: `${color}1a`, color, '& svg': { fontSize: 18 },
        }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>{rows.length} حساب</Typography>
      </Box>

      <Box>
        {rows.map((r, i) => {
          const pct = Math.min(100, (Math.abs(Number(r.amount)) / base) * 100);
          return (
            <Box
              key={r.accountId ?? `${r.accountCode}-${i}`}
              onClick={onRowClick && r.accountId ? () => onRowClick(r) : undefined}
              title={onRowClick && r.accountId ? 'اضغط لفتح كشف الحساب' : undefined}
              sx={{
                display: 'grid', gridTemplateColumns: '84px minmax(0,1fr) auto', alignItems: 'center', gap: 1.25,
                px: 2, py: 1, borderBottom: '1px solid #f1f5f9',
                cursor: onRowClick && r.accountId ? 'pointer' : 'default',
                '&:hover': { bgcolor: '#f8faff' },
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#64748b' }}>{r.accountCode}</Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.accountName}
                </Typography>
                <Box sx={{ mt: .5, height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                  <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, opacity: .55 }} />
                </Box>
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, direction: 'ltr', color: Number(r.amount) < 0 ? '#be123c' : '#0f172a' }}>
                  {fmtMoney(r.amount)}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#94a3b8', direction: 'ltr' }}>{pct.toFixed(1)}%</Typography>
              </Box>
            </Box>
          );
        })}
        {!rows.length && (
          <Typography sx={{ py: 3, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>{emptyText}</Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, bgcolor: `${color}0f`, borderTop: `2px solid ${color}` }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>إجمالي {title}</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 17, color, direction: 'ltr' }}>{fmtMoney(total)}</Typography>
      </Box>
    </Paper>
  );
}

/** نفس القسم للطباعة */
export function PrintFinancialSection({ title, rows = [], total }) {
  return (
    <>
      <div style={P.section}>{title}</div>
      <table style={P.table}>
        <thead>
          <tr>
            <th style={{ ...P.th, width: 80 }}>رقم الحساب</th>
            <th style={P.th}>اسم الحساب</th>
            <th style={{ ...P.th, width: 110 }}>المبلغ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.accountId ?? i}>
              <td style={P.td}>{r.accountCode}</td>
              <td style={P.td}>{r.accountName}</td>
              <td style={P.num}>{fmtMoney(r.amount)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={3} style={{ ...P.td, textAlign: 'center', color: P.colors.muted }}>لا توجد بيانات</td></tr>}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} style={{ ...P.th, ...P.total }}>إجمالي {title}</td>
            <td style={{ ...P.num, ...P.total }}>{fmtMoney(total)}</td>
          </tr>
        </tfoot>
      </table>
    </>
  );
}
